import { BasicServiceConnection, SubscriberFunctionInterface } from '@App/sockets/basic_connection'
import * as types from '@App/sockets/types'
import { WebsocketClientInterface } from '@App/sockets/websocket_context'

const UserServiceDestination = 'go.user_service'

class ProxyServiceConnection extends BasicServiceConnection implements WebsocketClientInterface {
  public name: string = types.ServiceNames.ProxyServiceName
  public path: string = ''
  public wallet_ids: number[] = []
  public account_ids: number[] = []

  clearData(): void {
    this.token = ''
    this.wallet_ids = []
    this.account_ids = []
  }

  setApiUrl = (api_url: string) => {
    if (this.apiUrl !== api_url) {
      this.path = api_url
      this.apiUrl = api_url
      this.initialize = true

      if (this.token) {
        this.apiUrl = this.path + '?token=' + this.token
      }
    }
  }

  getUserEventSocketMessage(request_type: string) {
    return JSON.stringify({
      request_key: '' + window.performance.now(),
      destination: UserServiceDestination,
      request_type: request_type,
      event: 'get_user',
      payload: {},
    })
  }

  getUserWalletEventSocketMessage(request_type: string, payload: string) {
    const preparePayload = payload ? JSON.parse(payload) : {}

    switch (request_type) {
      case types.RequestTypes.Unsubscribe:
        this.wallet_ids = this.wallet_ids.filter((wallet_id) => wallet_id !== preparePayload?.wallet_id) ?? []
        break
      default:
        this.wallet_ids.push(preparePayload?.wallet_id)
        break;
    }

    return JSON.stringify({
      request_key: '' + window.performance.now(),
      destination: UserServiceDestination,
      request_type: request_type,
      event: types.Events.GetUserWalletEvent,
      payload: preparePayload,
    })
  }

  getUserOrdersEventSocketMessage(request_type: string, payload?: string) {
    const preparePayload = payload ? JSON.parse(payload) : {}

    switch (request_type) {
      case types.RequestTypes.Unsubscribe:
        this.account_ids = this.account_ids.filter((account_id) => account_id !== preparePayload?.account_id) ?? []
        break
      default:
        this.account_ids.push(preparePayload?.account_id)
        break;
    }

    return JSON.stringify({
      request_key: '' + window.performance.now(),
      destination: UserServiceDestination,
      request_type: request_type,
      event: types.Events.GetUserOrdersEvent,
      payload: preparePayload,
    })
  }

  getUpdateTokenSocketMessage(token: string) {
    return JSON.stringify({
      request_key: '' + window.performance.now(),
      destination: 'pubsub',
      request_type: 'request',
      event: 'update_token',
      payload: { token },
    })
  }

  updateTokenActions(token: string) {
    this.isConnected() &&
      this.getConnection().send(
        JSON.stringify({
          request_key: '' + window.performance.now(),
          destination: 'pubsub',
          request_type: 'request',
          event: 'update_token',
          payload: { token },
        }),
      )
  }

  subscribe([token, payload]: string[], event: string, func: SubscriberFunctionInterface): void {
    try {
      if (this.token !== token && token) {
        this.token = token
        if (this.path) {
          this.apiUrl = this.path + '?token=' + token
        }
      }

      if (!this.isConnected() && !this.connection) {
        this.connection = this.establishConnection(func)
      }

      switch (event) {
        case types.Events.GetUserEvent:
          this.connected().then(() => {
            this.getConnection().send(this.getUserEventSocketMessage(types.RequestTypes.Subscribe))
          })
          break
        case types.Events.UpdateToken:
          this.connected().then(() => {
            this.getConnection().send(this.getUpdateTokenSocketMessage(token))
          })
          break
        case types.Events.GetUserWalletEvent:
          this.connected().then(() => {
            this.getConnection().send(this.getUserWalletEventSocketMessage(types.RequestTypes.Subscribe, payload))
          })
          break
        case types.Events.GetUserOrdersEvent:
          this.connected().then(() => {
            this.getConnection().send(this.getUserOrdersEventSocketMessage(types.RequestTypes.Subscribe, payload))
          })
          break
      }
    } catch (error) {
      console.log(error)
    }
  }

  unsubscribe([_, payload]: string[], event: string): void {
    if (this.connection) {
      switch (event) {
        case types.Events.GetUserEvent:
          this.connected().then(() => {
            if (this.connection.readyState === 1) {
              this.getConnection().send(this.getUserEventSocketMessage(types.RequestTypes.Unsubscribe))
            }
          })
          break
        case types.Events.GetUserWalletEvent:
          this.connected().then(() => {
            this.getConnection().send(this.getUserWalletEventSocketMessage(types.RequestTypes.Unsubscribe, payload))
          })
          break
        case types.Events.GetUserOrdersEvent:
          this.connected().then(() => {
            this.getConnection().send(this.getUserOrdersEventSocketMessage(types.RequestTypes.Unsubscribe))
          })
          break
      }
    }
  }
}

export default new ProxyServiceConnection()
